import { useEffect, useState } from 'react';

import {
  Checkbox,
  Divider,
  Heading,
  Label,
  LabeledControl,
  Modal,
  Paragraph,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';

import { useRentalManagerUserPropertyDigitallyAlteredPhotosWebApiHandler_attest } from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { EXTERNAL_URLS } from '@zg-rentals/zillow-rental-manager/constants';

import { AI_IMAGE_LISTING_COMPLIANCE_DETAILS_TAG } from '../../../hooks/queries/useAiImageListingCompliance';

import ModalFooter from '../ModalFooter';

type Props = {
  listingAlias: string;
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
  submitText?: string;
};

const DigitallyAlteredPhotosAttestationModal = ({
  listingAlias,
  isOpen,
  onClose,
  onContinue,
  submitText = 'Continue to photos',
}: Props) => {
  const { enqueueToast } = useToast();
  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    if (!isOpen) setIsChecked(false);
  }, [isOpen]);

  const { mutateAsync: attest, isPending } = useRentalManagerUserPropertyDigitallyAlteredPhotosWebApiHandler_attest({
    meta: {
      invalidates: [AI_IMAGE_LISTING_COMPLIANCE_DETAILS_TAG],
      context: { api: 'digitallyAlteredPhotosAttest', details: { listingAlias } },
    },
  });

  const onSubmit = async () => {
    try {
      await attest({ postData: { listingAlias } });
      onContinue();
    } catch {
      enqueueToast(<Toast tone="critical">Your acknowledgment couldn’t be saved. Refresh and try again.</Toast>);
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={(open) => !open && onClose()} size={{ base: 'fullScreen', md: 'md' }}>
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content>
            <Modal.Header>
              <Heading level={2}>California photo review</Heading>
            </Modal.Header>
            <Divider />
            <Modal.Body>
              <div data-testid="DigitallyAlteredPhotosAttestationModal">
                <Paragraph css={{ marginBottom: '400' }}>
                  California AB 723 requires certain real-estate sales ads to label digitally altered photos and give
                  viewers access to the original images.
                </Paragraph>

                <Paragraph css={{ marginBottom: '400' }}>
                  <strong>What counts:</strong> Adding, removing, or changing furniture, property features, or views —
                  for example, virtually staging a room, removing a wall, or changing the view outside a window.
                </Paragraph>

                <Paragraph css={{ marginBottom: '400' }}>
                  <strong>What doesn’t:</strong> Routine lighting adjustments or cropping, as long as they don’t change
                  how the property is represented.
                </Paragraph>

                <TextButton tone="brand" asChild>
                  <a href={EXTERNAL_URLS.CA_AB_723} target="_blank" rel="noopener noreferrer">
                    Read California AB 723 on the official legislative site
                  </a>
                </TextButton>

                <LabeledControl
                  css={{ marginTop: '400' }}
                  controlId="digitally-altered-photos-attestation"
                  label={<Label>I will mark photos that have been digitally altered</Label>}
                  control={<Checkbox checked={isChecked} onChange={() => setIsChecked((prev) => !prev)} />}
                />
              </div>
            </Modal.Body>
            <Divider />
            <Modal.Footer>
              <ModalFooter
                submitText={submitText}
                isSubmitDisabled={!isChecked}
                isSubmitting={isPending}
                onSubmit={onSubmit}
              />
            </Modal.Footer>
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
};

export default DigitallyAlteredPhotosAttestationModal;
